import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

/**
 * Accessible custom select — replaces native <select> for role/status pickers.
 * options: [{ value, label, description?, disabled? }]
 */
export default function Select({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  error,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)) ?? null,
    [options, value]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label ? (
        <label className="mb-1.5 block text-xs font-semibold text-primary/50">{label}</label>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${
          open
            ? "border-accent/50 ring-2 ring-accent/20"
            : "border-primary/10 hover:border-primary/20"
        } ${disabled ? "cursor-not-allowed bg-primary/[0.03] opacity-60" : "bg-white"} ${
          error ? "border-red-400" : ""
        }`}
      >
        <span className="min-w-0">
          <span className={`block truncate font-semibold ${selected ? "text-primary" : "text-primary/35"}`}>
            {selected?.label ?? placeholder}
          </span>
          {selected?.description ? (
            <span className="mt-0.5 block truncate text-[11px] text-primary/40">{selected.description}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-primary/40 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1.5 max-h-60 w-full overflow-auto rounded-xl border border-primary/[0.08] bg-white py-1 shadow-xl shadow-primary/10"
        >
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <li key={String(opt.value)} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors disabled:opacity-40 ${
                    isSelected ? "bg-collect/[0.06]" : "hover:bg-primary/[0.03]"
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-primary">{opt.label}</span>
                    {opt.description ? (
                      <span className="mt-0.5 block text-[11px] text-primary/45">{opt.description}</span>
                    ) : null}
                  </span>
                  {isSelected ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-collect" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
